import { useCallback, useEffect, useMemo, useState } from 'react'
import { subMinutes } from 'date-fns'
import getAgent from '../utils/getAgent'
import useConfig from './useConfig'
import useToken from './useToken'
import LedgerScannerClient from '../clients/LedgerScannerClient'
import { PriceQuote, TimeFrame } from '../interfaces/tokenPriceInfo'
import { AnalyticsDetails } from '../interfaces/common'
import OdinClient from '../clients/OdinClient'
import PhantomDefiClient from '../clients/PhantomDefiClient'

export interface Range {
  value: number
  label: string
  frame: TimeFrame
}

interface Props {
  tokenId?: string
  range: Range
}

const useTokenPriceInfo = ({ tokenId, range }: Props) => {
  const [priceQuotes, setPriceQuotes] = useState<PriceQuote[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const token = useToken(tokenId)
  const { tokenPricesWithChange, tokenGroups } = useConfig()

  const agent = useMemo(() => {
    return getAgent()
  }, [])

  const filterQuotes = (quotes: PriceQuote[]) => {
    const now = new Date()
    switch (range.label) {
      case '1H':
        return []
      case '1D':
        now.setHours(now.getHours() - 24)
        break
      case '1W':
        now.setDate(now.getDate() - 7)
        break
      case '1M':
        now.setMonth(now.getMonth() - 1)
    }

    const startTimestamp = now.getTime()
    return quotes.filter((q) => q.timestamp >= startTimestamp)
  }

  const requestTokenQuotes = async (analyticsDetails?: AnalyticsDetails) => {
    switch (analyticsDetails?.analyticsProvider) {
      case 'ledgerscanner':
        const lsQuotes = await LedgerScannerClient.fetchTokenPriceHistory(
          analyticsDetails?.analyticsId || ''
        )
        return filterQuotes(lsQuotes)
      case 'phantom':
        return PhantomDefiClient.getPriceHistory(analyticsDetails.analyticsId || '', range.label)
      case 'odin':
        const startDate = subMinutes(new Date(), range.value)
        return OdinClient.fetchTokenPriceHistory(
          analyticsDetails?.analyticsId || '',
          startDate,
          new Date(),
          tokenPricesWithChange.btc?.price
        )
      default:
        return []
    }
  }

  const fetchQuotes = useCallback(async () => {
    setIsLoading(true)

    if (!token || !range) {
      setIsLoading(false)
      return
    }

    const quotes = await requestTokenQuotes(
      token.groupId
        ? tokenGroups.find((g) => g.id === token.groupId)?.analyticsDetails
        : token.analyticsDetails
    )

    setPriceQuotes(quotes)
    setIsLoading(false)
  }, [token, range.value, range.frame, range.label, tokenGroups])

  useEffect(() => {
    fetchQuotes()
  }, [token, range.value, agent, range.frame, range.label])

  return {
    priceQuotes,
    price: tokenPricesWithChange[token?.id || '']?.price,
    isLoading,
  }
}

export default useTokenPriceInfo
